import React, { useRef } from 'react';
import BaseLayout from '../../components/layout/BaseLayout';
import styles from './AboutPage.module.css';
import { FaTerminal } from 'react-icons/fa';
import ExpertSection from '../../components/sections/Expert/ExpertSection';
import RecognitionSection from '../../components/sections/Recognition/RecognitionSection';
import { useTranslation } from '../../hooks/useTranslation';

import SEOMetaTags from '../../components/SEO/SEOMetaTags';

// Import images
import photoAdam from '../../assets/images/team/photo-adam.png';
import photoMateusz from '../../assets/images/team/photo-mateusz.png';
import photoOskar from '../../assets/images/team/photo-oskar.png';
import photoPatryk from '../../assets/images/team/photo-patryk.png';
import photoTomasz from '../../assets/images/team/photo-tomasz.png';
import photoKatarzyna from '../../assets/images/team/photo-katarzyna.png';
import photoNatalia from '../../assets/images/team/photo-natalia.png';
import photoArtur from '../../assets/images/team/photo-artur.png';
import photoMaciejL from '../../assets/images/team/photo-MaciejL.png';
import photoLeonard from '../../assets/images/team/photo-leonard.png';
import photoBogdan from '../../assets/images/team/photo-bogdan.png';
import photoFilip from '../../assets/images/team/photo-filip.png';
import photoSeweryn from '../../assets/images/team/photo-seweryn.png';
import photoOskarJr from '../../assets/images/team/photo-OskarJr.png';
import photoAleks from '../../assets/images/team/photo-Aleks.png';
import Button from '../../components/Button/Button';
import { MdKeyboardArrowRight } from 'react-icons/md';
import HeroSectionStatic from '../../components/sections/Hero/HeroSectionStatic';
import recognitionImage from '../../assets/images/team/Appotype-mateusz-chechlinski-wyroznienie.png';
import speakerImage from '../../assets/images/team/Appotype-mateusz-chechlinski-przemowienie.png';
import strategyImage from '../../assets/images/team/Appotype-mateusz-chechlinski-networking.png';
import { isSmallScreen } from '../../utils/screen';

const AboutPage: React.FC = () => {
  const { t } = useTranslation();
  const valuesRef = useRef<HTMLDivElement>(null);
  
  const teamMembers = [
    {
      name: "Mateusz",
      role: t("team.role.ceo"),
      image: photoMateusz
    },
    {
      name: "Natalia",
      role: t("team.role.cfo"),
      image: photoNatalia
    },
    {
      name: "Artur",
      role: t("team.role.consultant"),
      image: photoArtur
    },
    {
      name: "Katarzyna",
      role: t("team.role.headOfDelivery"),
      image: photoKatarzyna
    },
    {
      name: "Oskar",
      role: t("team.role.developer"),
      image: photoOskar
    },
    {
      name: "Adam",
      role: t("team.role.developer"),
      image: photoAdam
    },
    {
      name: "Patryk",
      role: t("team.role.developer"),
      image: photoPatryk
    },
    {
      name: "Filip",
      role: t("team.role.developer"),
      image: photoFilip
    },
    {
      name: "Seweryn",
      role: t("team.role.developer"),
      image: photoSeweryn
    },
    {
      name: "Oskar",
      role: t("team.role.developer"),
      image: photoOskarJr
    },
    {
      name: "Maciej",
      role: t("team.role.qa"),
      image: photoMaciejL
    },
    {
      name: "Bogdan",
      role: t("team.role.ai"),
      image: photoBogdan
    },
    {
      name: "Aleks",
      role: t("team.role.ai"),
      image: photoAleks
    },
    {
      name: "Leonard",
      role: t("team.role.3dArtist"),
      image: photoLeonard
    },
    {
      name: "Tomasz",
      role: t("team.role.designer"),
      image: photoTomasz
    },
  ];

  const values = [
    {
      title: t("about.values.card1.title"),
      description: [
        t("about.values.card1.description1"),
        t("about.values.card1.description2")
      ]
    },
    {
      title: t("about.values.card2.title"),
      description: [
        t("about.values.card2.description1"),
        t("about.values.card2.description2")
      ]
    },
    {
      title: t("about.values.card3.title"),
      description: [
        t("about.values.card3.description1"),
        t("about.values.card3.description2")
      ]
    },
    {
      title: t("about.values.card4.title"),
      description: [
        t("about.values.card4.description1"),
        t("about.values.card4.description2")
      ]
    },
    {
      title: t("about.values.card5.title"),
      description: [
        t("about.values.card5.description1"),
        t("about.values.card5.description2")
      ]
    }
  ];

  return (
    <>
      <SEOMetaTags
        title={{
          pl: "O Nas - Appotype | Poznaj Zespół Ekspertów IT i Nasze Wartości",
          en: "About Us - Appotype | Meet Our IT Expert Team & Values"
        }}
        description={{
          pl: "Poznaj Appotype - zespół ekspertów IT gotowych zrealizować Twoje wizje technologiczne. Dowiedz się więcej o naszym podejściu, wartościach (terminowość, szczerość, odpowiedzialność) i zespole.",
          en: "Meet Appotype - a team of IT experts ready to realize your technological visions. Learn more about our approach, values (Timeliness, Honesty, Responsibility), and team."
        }}
        canonicalUrl="/about"
      />
      <BaseLayout>
        <HeroSectionStatic
          title={t("about.hero.title")}
          subtitle={t("about.hero.subtitle")}
          heroButtons={[
            {
              label: t("about.hero.button"),
              variant: "secondary",
              onClick: () => {
                if (valuesRef.current) {
                  const elementPosition = valuesRef.current.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - 64;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }
            }
          ]}
        />

        <section className={styles.values} ref={valuesRef}>
          <div className={styles.container}>
            <div className={styles.valuesHeader}>
              <FaTerminal size={54} />
              <div className={styles.valuesHeaderContent}>
                <h2 className={styles.sectionTitle}>
                  {t("about.values.title")}
                </h2>
                <p className={styles.sectionSubtitle}>
                  {t("about.values.subtitle")}
                </p>
              </div>
            </div>
            <div className={styles.valuesGrid}>
              {values.map((value, index) => (
                <div key={index} className={styles.valueCard}>
                  <h3 className={styles.valueTitle}>{value.title}</h3>
                  <div className={styles.valueDescription}>
                    {value.description.map((paragraph, pIndex) => (
                      <p key={pIndex} className={styles.valueDescriptionParagraph}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <RecognitionSection 
          title={t("about.recognition.title")}
          subtitle={[t("about.recognition.subtitle")]}
          recognitionImage={recognitionImage}
          speakerImage={speakerImage}
        />

        <ExpertSection
          title={t("about.expert.title")}
          description={t("about.expert.description")}
          quotes={[
            t("about.expert.quotes1"),
            t("about.expert.quotes2")
          ]}
          author={{
            name: "Mateusz Chechliński",
            role: t("team.role.ceo"),
            image: photoMateusz
          }}
        />

        <section className={styles.strategySection}>
          <div className={styles.rowContainer}>
            <div className={styles.strategyHeader}>
              <FaTerminal size={isSmallScreen() ? 44 : 54} />
              <div className={styles.strategyHeaderContent}>
                <h2 className={styles.sectionTitle}>
                  {t("about.strategy.title")}
                </h2>
                <p className={styles.sectionSubtitle}>
                  {t("about.strategy.subtitle")}
                </p>
                <Button variant="secondary" onClick={() => {window.location.href = '/contact'; }} rightIcon={<MdKeyboardArrowRight size={32} />} className={styles.joinTeamButton}>
                  {t("about.strategy.button")}
                </Button>
              </div>
            </div>
            <div className={styles.strategyImageContainer}>
              <img src={strategyImage} alt={t("alt.strategy")} />
            </div>
          </div>
        </section>

        <section className={styles.team}>
          <div className={styles.container}>
            <div className={styles.teamHeader}>
              <h2 className={styles.sectionTitle}>
                {t("about.team.title")}
              </h2>
              <p className={styles.sectionSubtitle}>
                {t("about.team.subtitle")}
              </p>
            </div>
            <div className={styles.teamGrid}>
              {teamMembers.map((member, index) => (
                <div key={index} className={styles.teamMember}>
                  <img src={member.image} alt={member.name} className={styles.memberImage} />
                  <div className={styles.memberInfo}>
                    <h3 className={styles.memberName}>{member.name}</h3>
                    <p className={styles.memberRole}>{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.joinTeam}>
          <div className={styles.container}>
            <div className={styles.joinTeamContent}>
              <div className={styles.joinTeamIcon}>
                <FaTerminal size={isSmallScreen() ? 44 : 54} />
              </div>
              <h2 className={styles.joinTeamTitle}>
                {t("about.joinTeam.title")}
              </h2>
              <p className={styles.joinTeamDescription}>
                {t("about.joinTeam.description")}
              </p>
              <Button variant="primary" onClick={() => {
                window.location.href = 'mailto:contact@appotype.eu';
               }} rightIcon={<MdKeyboardArrowRight size={32} />} className={styles.joinTeamButton}>
                {t("about.joinTeam.button")}
              </Button>
            </div>
          </div>
        </section>
      </BaseLayout>
    </>
  );
};

export default AboutPage;
